import { useCallback } from 'react';
import { useApiCall } from '@/hooks/useApiCall';
import { useApiClient } from '@/hooks/useApiClient';
import {
  signalMachinesReadAll,
  signalSourcesReadAll,
} from '@/features/signals/api/SignalsApiClient';
import type { SignalMachinesList, SignalSourcesList } from '@/features/signals/type/SignalsTypes';
import type { BaseResponse } from '@/lib/http/HttpClient';

type SignalMachinesResponse = BaseResponse & SignalMachinesList;
type SignalSourcesResponse = BaseResponse & SignalSourcesList;

/** The caller's machines with their signal source — fetched once on mount. */
export function useSignalMachines() {
  const http = useApiClient();

  const apiFn = useCallback(
    async (_signal: AbortSignal) => {
      const list = await signalMachinesReadAll(http ?? undefined);
      return { ok: true as const, data: { timestamp: new Date().toISOString(), ...list } };
    },
    [http]
  );

  const { data, isLoading, error, retry } = useApiCall<SignalMachinesResponse>({
    apiFn,
    immediate: true,
    deps: [],
  });

  return { data: data ?? null, isLoading, error, retry };
}

/** The names a machine can take as its signal source — fetched once on mount. */
export function useSignalSources() {
  const http = useApiClient();

  const apiFn = useCallback(
    async (_signal: AbortSignal) => {
      const list = await signalSourcesReadAll(http ?? undefined);
      return { ok: true as const, data: { timestamp: new Date().toISOString(), ...list } };
    },
    [http]
  );

  const { data, isLoading, error, retry } = useApiCall<SignalSourcesResponse>({
    apiFn,
    immediate: true,
    deps: [],
  });

  return { data: data ?? null, isLoading, error, retry };
}
